import { useRef, useEffect, useState } from 'react';

interface ConnectionScaleProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
}

const MIN_SINGLE_ROW_WIDTH = 10 * 44 + 9 * 4; // 476px: 10 buttons × 44px + 9 gaps × 4px

export default function ConnectionScale({ label, value, onChange }: ConnectionScaleProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [twoRowMode, setTwoRowMode] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setTwoRowMode(entry.contentRect.width < MIN_SINGLE_ROW_WIDTH);
      }
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const buttonStyle = (num: number): React.CSSProperties => ({
    width: '100%',
    aspectRatio: '1 / 1',
    minHeight: '44px',
    borderRadius: '50%',
    border: value === num ? '3px solid #4a90e2' : '2px solid #ccc',
    background: value === num ? '#4a90e2' : 'white',
    color: value === num ? 'white' : '#333',
    fontSize: 'clamp(12px, 2.5vw, 18px)',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.2s',
    padding: 0,
  });

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    gap: '4px',
  };

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(10, 1fr)',
    gap: '4px',
  };

  return (
    <div style={{ padding: '20px', background: '#f5f5f5', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
      <label style={{ display: 'block', marginBottom: '15px', fontWeight: 'bold', fontSize: '16px' }}>
        {label}
      </label>

      <div ref={containerRef}>
        {twoRowMode ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={rowStyle}>
              {[1, 2, 3, 4, 5].map((num) => (
                <button key={num} type="button" onClick={() => onChange(num)} style={{ ...buttonStyle(num), flex: '1 1 0' }}>
                  {num}
                </button>
              ))}
            </div>
            <div style={rowStyle}>
              {[6, 7, 8, 9, 10].map((num) => (
                <button key={num} type="button" onClick={() => onChange(num)} style={{ ...buttonStyle(num), flex: '1 1 0' }}>
                  {num}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div style={gridStyle}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
              <button key={num} type="button" onClick={() => onChange(num)} style={buttonStyle(num)}>
                {num}
              </button>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', fontSize: '14px', color: '#666' }}>
        <span>Brak kontaktu</span>
        <span>Pełny kontakt</span>
      </div>
    </div>
  );
}
